import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type DragEvent } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatDayText } from '../events/fields';
import { FUNCTION_SETTINGS_KEY as SETTINGS_KEY, FUNCTIONS_RESOURCE, PEOPLE_KEY, type FunctionSettings } from '../people/types';
import { dropOn } from '../quotes/lineOrder';
import { ConflictNotice } from '../ui/ConflictNotice';
import { CREW_KEY, CREW_RESOURCE } from './types';
import { refreshCrew } from './refreshCrew';
import { filledOf, filledText, infoValue, releasedText, type CountResult, type InfoFunction, type PositionInfo, type PositionsView } from './positions';
import { Notice } from '../ui/Notice';

/**
 * „ⓘ Info” of Obsada (owner 2026-10-26 07:36–07:40, like the quote's „ⓘ Info”): collapsed by default, the choice
 * remembered per person on this computer. Inside, one number field per function the global ⚙ shows („Realizator FOH 1,
 * Technik 3, Inni 0”), in the global order: how many places of it the event needs. A number from the rules is shown
 * with „z reguły” until one is typed. Changing a number adds or removes places (free places from the end first, then
 * the last people — „Zwolniono: …”). A multi-day event: the numbers apply to every day; the day switch sets one day.
 * ⚙ (the owner): which functions Info shows and their ORDER (drag) — a program setting, the same for every event and the
 * same order as Osoby → Funkcje.
 * `filled` (owner 2026-10-27 08:16, the grid's side panel — the counts left the bar): beside each number the places
 * filled of it (the chosen day, or the worst one).
 * `variant="panel"` (owner 2026-10-27 21:01, only the grid's side panel): one row per function — the number first, then
 * the function, then the count as just „1/2”. Obsada's tab keeps its layout (the function, then the number).
 */
const storeKey = (userId: string) => `crew.info.open:${userId}`;

function loadOpen(userId: string): boolean {
  try {
    return window.localStorage.getItem(storeKey(userId)) === '1';
  } catch {
    return false;
  }
}

function saveOpen(userId: string, open: boolean): void {
  try {
    window.localStorage.setItem(storeKey(userId), open ? '1' : '0');
  } catch {
    // storage blocked: it opens collapsed next time
  }
}

function errorText(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const details = Array.isArray(error.details) ? (error.details as { message?: unknown }[]).map((d) => d.message).filter((m): m is string => typeof m === 'string') : [];
  return details.length > 0 ? details.join('. ') : error.message;
}

export function ObsadaInfo({ eventId, onChanged, filled, variant = 'tab' }: { eventId: string; onChanged?: () => void; filled?: PositionsView | undefined; variant?: 'tab' | 'panel' }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(() => (me ? loadOpen(me.id) : false));
  const [day, setDay] = useState<string | null>(null);
  const [gear, setGear] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const id = useId();
  const info = useQuery({ queryKey: [...CREW_KEY, 'position-info', eventId], queryFn: () => api<PositionInfo>(`/api/crew/events/${eventId}/position-info`) });
  const mayEdit = !!me && can(me, CREW_RESOURCE, 'edit') && fieldVisible(me, CREW_RESOURCE, 'functionId');
  const setCount = useMutation({
    mutationFn: (b: { f: InfoFunction; count: number }) =>
      api<CountResult>(`/api/crew/events/${eventId}/position-counts`, {
        method: 'PUT',
        body: { functionId: b.f.others ? null : b.f.functionId, count: b.count, ...(info.data?.multiDay ? (day ? { day } : { allDays: true }) : {}) },
      }),
    onSuccess: async (r) => {
      setNotice(releasedText(r.released));
      await refreshCrew(queryClient);
      onChanged?.();
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: [...CREW_KEY, 'position-info', eventId] });
    },
  });
  if (!me) return null;
  const toggle = () => {
    setOpen(!open);
    saveOpen(me.id, !open);
  };
  const data = info.data;
  return (
    <div className={`q-info crew-info${open ? ' open' : ''}`} data-feature-id="crew.positions">
      <button className="q-info-toggle" type="button" aria-expanded={open} aria-controls={id} onClick={toggle}>
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
          <path d="M8 7.2v4M8 4.9v.1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        Info
        <svg className="q-info-chevron" width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div id={id} className="crew-info-body" role="group" aria-label="Informacje o obsadzie">
          <div className="crew-info-head">
            <span className="crew-info-title">Ile osób na stanowiskach</span>
            {data?.multiDay && (
              <select aria-label="Dzień" value={day ?? ''} onChange={(e) => setDay(e.target.value || null)}>
                <option value="">Wszystkie dni</option>
                {data.days.map((d) => (
                  <option key={d} value={d}>
                    {formatDayText(d)}
                  </option>
                ))}
              </select>
            )}
            {me.isOwner && (
              <button className={`btn ghost small icon${gear ? ' active' : ''}`} type="button" aria-label="Ustawienia funkcji w Info" aria-expanded={gear} title="Które funkcje i w jakiej kolejności" onClick={() => setGear(!gear)}>
                ⚙
              </button>
            )}
          </div>
          {gear && me.isOwner && <FunctionSettingsEditor />}
          {data && data.functions.length === 0 && <p className="muted crew-info-empty">{me.isOwner ? 'Wybierz w ⚙, które funkcje pokazywać tutaj.' : 'Właściciel nie wybrał jeszcze funkcji (⚙).'}</p>}
          {data && data.functions.length > 0 && (() => {
            const fields = data.functions.map((f) => (
              <CountField key={`${f.functionId}:${day ?? 'all'}:${f.count}:${f.source}:${f.mixed}`} f={f} day={day} row={variant === 'panel'} editable={mayEdit} busy={setCount.isPending} onSet={(count) => setCount.mutate({ f, count })} filled={filled ? filledOf(filled, f.functionId, day) : null} />
            ));
            return variant === 'panel' ? (
              <ul className="crew-info-counts crew-info-rows" aria-label="Ile osób na stanowiskach">
                {fields}
              </ul>
            ) : (
              <dl className="q-meta crew-info-counts">{fields}</dl>
            );
          })()}
          {notice && (
            <Notice className="crew-positions-notice" onClose={() => setNotice(null)}>
              {notice}
            </Notice>
          )}
          {setCount.error && (
            <p className="alert" role="alert">
              {errorText(setCount.error, 'Nie udało się zmienić liczby')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One function's number: typed and committed on leaving the field (or Enter). `row` (the side panel): a list row —
 * the number, the function, „z reguły”, the count „1/2”.
 */
function CountField({ f, day, row = false, editable, busy, onSet, filled }: { f: InfoFunction; day: string | null; row?: boolean; editable: boolean; busy: boolean; onSet: (count: number) => void; filled: { filled: number; needed: number } | null }) {
  const value = infoValue(f, day);
  const [text, setText] = useState(value.count === null ? '' : String(value.count));
  const commit = () => {
    const t = text.trim();
    if (t === '' || !/^\d+$/.test(t)) {
      setText(value.count === null ? '' : String(value.count));
      return;
    }
    const n = Number(t);
    // a number shown „z reguły” typed as it is becomes the owner's number too
    if (n === value.count && value.source === 'manual') return;
    onSet(n);
  };
  const number = editable ? (
    <input
      className="crew-info-count"
      type="text"
      inputMode="numeric"
      aria-label={f.functionName}
      value={text}
      placeholder={value.count === null ? 'różne' : undefined}
      disabled={busy}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
    />
  ) : (
    <span className="crew-info-number">{value.count ?? 'różne'}</span>
  );
  const source = value.source === 'rule' && <span className="crew-position-source">z reguły</span>;
  const count = filled && <span className="crew-info-filled">{filledText(filled)}</span>;
  if (row) {
    return (
      <li className="crew-info-row" aria-label={f.functionName}>
        {number}
        <span className="crew-info-name">{f.functionName}</span>
        {source}
        {count}
      </li>
    );
  }
  return (
    <div>
      <dt>{f.functionName}</dt>
      <dd>
        {number}
        {source}
        {count}
      </dd>
    </div>
  );
}

/**
 * ⚙ (the owner): which functions Info shows (checkboxes, „Inni” too) and their order (drag the ⋮⋮ grip) — saved at once,
 * for every event; the same order as Osoby → Funkcje.
 */
export function FunctionSettingsEditor() {
  const queryClient = useQueryClient();
  const { me } = useMe();
  const settings = useQuery({ queryKey: SETTINGS_KEY, enabled: !!me && can(me, FUNCTIONS_RESOURCE, 'view'), queryFn: () => api<FunctionSettings>('/api/people/function-settings') });
  const [drag, setDrag] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (b: { order: string[]; visible: string[]; version: number }) => api<FunctionSettings>('/api/people/function-settings', { method: 'PUT', body: b }),
    // shown at once (the box ticked, the row moved); the server's answer replaces it
    onMutate: (b) => {
      const now = queryClient.getQueryData<FunctionSettings>(SETTINGS_KEY);
      if (!now) return;
      const byId = new Map(now.order.map((f) => [f.id, f]));
      queryClient.setQueryData<FunctionSettings>(SETTINGS_KEY, { ...now, order: b.order.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : [])), visible: b.visible });
    },
    onSuccess: (r) => {
      queryClient.setQueryData(SETTINGS_KEY, r);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: PEOPLE_KEY });
      await refreshCrew(queryClient);
    },
  });
  const s = settings.data;
  if (!s) return settings.error ? <p className="alert" role="alert">{errorText(settings.error, 'Nie udało się wczytać ustawień')}</p> : null;
  const ids = s.order.map((f) => f.id);
  const shown = new Set(s.visible);
  const put = (order: string[], visible: Set<string>) => save.mutate({ order, visible: order.filter((x) => visible.has(x)), version: s.version });
  const conflict = save.error instanceof ApiError && save.error.status === 409;
  return (
    <section className="crew-info-settings" aria-label="Funkcje w Info">
      <p className="muted">Zaznacz funkcje, które Info pokazuje, i przeciągnij je w kolejności. Ustawienie jest wspólne dla wszystkich wydarzeń (ta sama kolejność co w Osoby → Funkcje).</p>
      <ul className="crew-info-order" aria-label="Kolejność funkcji">
        {s.order.map((f) => (
          <li
            key={f.id}
            aria-label={f.name}
            className={drag === f.id ? 'dragging' : undefined}
            onDragOver={(e: DragEvent<HTMLLIElement>) => {
              if (!drag) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e: DragEvent<HTMLLIElement>) => {
              if (!drag || save.isPending) return;
              e.preventDefault();
              const box = e.currentTarget.getBoundingClientRect();
              const next = dropOn(ids, drag, f.id, e.clientY > box.top + box.height / 2);
              setDrag(null);
              if (next) put(next, shown);
            }}
          >
            <span
              className="crew-position-grip"
              draggable={!save.isPending}
              aria-hidden="true"
              title="Przeciągnij, żeby zmienić kolejność"
              onDragStart={(e) => {
                // review M4: not while a save is pending (the next one needs the version it returns)
                if (save.isPending) return e.preventDefault();
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', f.id);
                setDrag(f.id);
              }}
              onDragEnd={() => setDrag(null)}
            />
            <label className="check">
              <input
                type="checkbox"
                checked={shown.has(f.id)}
                disabled={save.isPending}
                onChange={(e) => {
                  const next = new Set(shown);
                  if (e.target.checked) next.add(f.id);
                  else next.delete(f.id);
                  put(ids, next);
                }}
              />
              <span>{f.name}</span>
            </label>
          </li>
        ))}
      </ul>
      {conflict && <ConflictNotice onReload={() => void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })} />}
      {save.error && !conflict && (
        <p className="alert" role="alert">
          {errorText(save.error, 'Nie udało się zapisać')}
        </p>
      )}
    </section>
  );
}
